import { hideLoadingSpinner, showLoadingSpinner } from 'admin/actions/loading-spinner';
import {
  getOrganizationPrivateMoneyFee,
  showOrganization,
  updateOrganization,
  updateOrganizationPrivateMoneyFee,
} from 'admin/actions/organization';
import { Button } from 'admin/components/atoms/Buttons';
import { convertFlicoStringFormat } from 'admin/utils';
import _ from 'lodash';
import React, { Component } from 'react';
import CSSModules from 'react-css-modules';
import { connect } from 'react-redux';
import { Alert } from 'reactstrap';
import { bindActionCreators } from 'redux';
import styled, { css } from 'styled-components';
import { toJapaneseBankingSystemHalfWidth } from '~/javascripts/admin/utils/characters/toHalfWidth';
import toKatakana from '~/javascripts/admin/utils/characters/toKatakana';
import Grid from '../../atoms/Grid';
import Input from '../../atoms/Input';
import Label from '../../atoms/Label';
import LocalizedMask from '../../atoms/LocalizedMask';
import Radio from '../../atoms/Radio';
import Required from '../../atoms/Required';
import Select from '../../atoms/Select';
import TextArea from '../../atoms/TextArea';
import BreadCrumb from '../../molecules/BreadCrumb';
import styles from './styles.scss';

const buildCrumbs = (user) => {
  const isMemberOrgOnly =
    !!(user?.isOrgUser?.() && !user.isServiceUser?.() && !user.isIssuerOf?.());
  const base = [
    { value: 'ホーム', href: '/', isCurrentPage: false },
    { value: '決済加盟店管理', href: '', isCurrentPage: true },
  ];
  // 一覧 (/organizations) は加盟組織権限ではアクセス不可なので、その場合は crumb を出さない
  if (!isMemberOrgOnly) {
    base.push({
      value: '決済加盟店一覧',
      href: '/organizations',
      isCurrentPage: false,
    });
  }
  base.push({ value: '決済加盟店詳細', href: '', isCurrentPage: true });
  return base;
};

const FormGrid = styled(Grid.Container)`
  margin: 16px 0;

  ${Label} {
    margin-bottom: 0;
  }

  ${Input}, ${TextArea}, ${Grid.Divider} {
    margin-bottom: 1rem;
  }
`;

const InputWithErrorWrapper = styled.div`
  ${({ theme: { spacing } }) => css`
    margin-bottom: ${spacing['16']};
    display: flex;
    flex-direction: column;

    ${Input}, ${TextArea} {
      margin-bottom: ${spacing['0']};
    }
  `}
`;

const RadioGroupWrapper = styled.div`
  display: flex;
  gap: 12px;
`;

const RadioWrapper = styled.div`
  display: flex;
  gap: 4px;
`;

const OrgMoneyFeeWrapper = styled.div`
  border: 1px solid #e8e8e8;
  border-radius: 3px;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.75rem 0.5rem;
  margin-bottom: 0.5rem;
`;

const OrgMoneyFeeInputRowWrapper = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const OrgMoneyFeeInputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;

  ${Input} {
    margin-bottom: 0;
  }
`;

const ErrorText = styled.span`
  ${({ theme: { colors } }) => css`
    color: ${colors.red['400']};
  `}
`;

class UpdateMemberOrganization extends Component {
  constructor(props) {
    super(props);
    this.state = {
      updateResponse: {},
      org: {
        code: '',
        name: '',
        business_type: null,
        corporate_number: null,
        bank_name: '',
        bank_code: '',
        bank_branch_name: '',
        bank_branch_code: '',
        bank_account_type: '',
        bank_account: '',
        bank_account_holder_name: '',
        contact_name: '',
        private_money_ids: [],
      },
      moneyOptions: [],
      selectedMoneyOptions: [],
      moneyFees: {},
      isLoadingFees: true,
      validationErrors: {},
    };
  }

  componentDidMount() {
    showOrganization(this.props.match.params.id).then((org) => {
      const alreadyMemberMoneyNames = org.private_moneys.map((m) => m.name);
      const moneyOptions = this.toMoneyOptions(this.props.user.issuedThirdPartyMoneys());
      const selectedMoneyOptions = moneyOptions.filter((op) =>
        alreadyMemberMoneyNames.includes(op.label)
      );

      this.setState({ org, moneyOptions, selectedMoneyOptions });

      // Fetch fee data for each private money after succesful organization call
      const feePromises = org.private_moneys.map((money) =>
        getOrganizationPrivateMoneyFee(org.code, money.id)
          .then((feeData) => ({
            moneyId: money.id,
            fees: {
              payment_fee_rate_percentage: feeData.payment_fee_rate_percentage.toString(),
              system_fee: feeData.system_fee.toString(),
              fixed_fee: feeData.fixed_fee.toString(),
            },
          }))
          .catch((error) => {
            console.error(`Error fetching fees for money ${money.id}:`, error);
            return {
              moneyId: money.id,
              fees: {
                payment_fee_rate_percentage: '0',
                system_fee: '0',
                fixed_fee: '0',
              },
            };
          })
      );

      Promise.all(feePromises).then((results) => {
        const moneyFees = {};
        results.forEach((result) => {
          moneyFees[result.moneyId] = result.fees;
        });
        this.setState({ moneyFees, isLoadingFees: false });
      });
    });
  }

  onSelectMoney = (options) => {
    this.setOrg(
      'private_money_ids',
      options.map((op) => op.value.id)
    );
    this.setState({ selectedMoneyOptions: options });
  };

  setMoneyFee = (moneyId, field, value) => {
    if (field === 'payment_fee_rate_percentage') {
      if (value !== '' && !/^\d*\.?\d*$/.test(value)) {
        return;
      }

      if (value.length > 1 && value[0] === '0' && value[1] !== '.') {
        value = value.replace(/^0+/, '');
      }

      const numValue = parseFloat(value);
      if (!isNaN(numValue) && numValue > 100) {
        return;
      }
    }

    this.setState((prevState) => ({
      moneyFees: {
        ...prevState.moneyFees,
        [moneyId]: {
          ...prevState.moneyFees[moneyId],
          [field]: value,
        },
      },
    }));
  };

  validateForm() {
    const errors = {};

    if (!this.state.org.name.trim()) {
      errors.name = '組織名を入力してください';
    }

    if (this.state.org.business_type === 'corporate') {
      const corporateNumber = (this.state.org.corporate_number ?? '').trim();
      if (!corporateNumber) {
        errors.corporate_number = '法人番号を入力してください';
      } else if (!/^\d{13}$/.test(corporateNumber)) {
        errors.corporate_number = '法人番号は13桁の半角数字で入力してください';
      }
    }

    return errors;
  }

  onClick = (e) => {
    e.preventDefault();

    const errors = this.validateForm();

    if (Object.keys(errors).length > 0) {
      this.setState({ validationErrors: errors });
      return;
    }

    this.setState({ validationErrors: {} });

    this.setOrg(
      'bank_account_holder_name',
      convertFlicoStringFormat(this.state.org.bank_account_holder_name)
    );
    this.onConfirm();
  };

  buildUpdatePayload() {
    // corporate_number は法人のときのみ送信する。個人事業主/未設定のときは空文字を送り、
    // 既存の invoice を残したまま（判断⑤: 空/未送信では削除しない）新規 upsert を避ける。
    // business_type が未設定(null)の場合も空文字を送り、サーバ側でメタデータ未記録扱いにする。
    const isCorporate = this.state.org.business_type === 'corporate';
    return {
      ...this.state.org,
      business_type: this.state.org.business_type ?? '',
      corporate_number: isCorporate ? (this.state.org.corporate_number ?? '').trim() : '',
    };
  }

  onConfirm = () => {
    this.props.showLoadingSpinner();

    updateOrganization(this.props.match.params.id, this.buildUpdatePayload())
      .then((json) => {
        this.setState({ updateResponse: json });

        if (this.state.org.code === json.code) {
          const feePromises = this.state.selectedMoneyOptions.map((money) => {
            const feeData = this.state.moneyFees[money.value.id];
            const paymentFee = parseFloat(feeData.payment_fee_rate_percentage);
            return updateOrganizationPrivateMoneyFee(json.code, money.value.id, {
              payment_fee_rate_percentage: paymentFee || 0,
              topup_fee_rate_percentage: 0,
              point_payment_fee_rate_percentage: paymentFee || 0,
              system_fee: parseFloat(feeData.system_fee) || 0,
              fixed_fee: parseFloat(feeData.fixed_fee) || 0,
            });
          });

          return Promise.all(feePromises)
            .then(() => {
              this.setState({ updateResponse: { ...json, feesUpdated: true } });
              this.props.hideLoadingSpinner();
            })
            .catch((error) => {
              this.setState({ updateResponse: { ...json, feesUpdated: false } });
              this.props.hideLoadingSpinner();
              console.error('Error updating fees:', error);
            });
        } else {
          this.props.hideLoadingSpinner();
        }
      })
      .catch((error) => {
        this.props.hideLoadingSpinner();
        console.error('Error updating organization:', error);
      });
  };

  isInvalidParameter(param) {
    return (
      !_.isEmpty(this.state.updateResponse) &&
      this.state.updateResponse.type === 'invalid_parameters' &&
      this.state.updateResponse.errors.invalid &&
      this.state.updateResponse.errors.invalid.indexOf(param) !== -1
    );
  }

  isSuccess() {
    return (
      !_.isEmpty(this.state.updateResponse) &&
      this.state.org.code === this.state.updateResponse.code &&
      this.state.updateResponse.feesUpdated === true
    );
  }

  setOrg(property, value) {
    this.setState({ org: { ...this.state.org, [property]: value } });
  }

  toMoneyOptions = (moneys) => moneys.map((m) => ({ label: m.name, value: m }));

  render() {
    return (
      <div className={this.props.className}>
        <BreadCrumb crumbs={buildCrumbs(this.props.user)} />
        <div styleName="headline">決済加盟店詳細</div>
        <form onSubmit={this.onClick}>
          <FormGrid>
            {/* 基本情報 section */}
            <Grid.Item size={2}>基本情報</Grid.Item>

            <Grid.Item size={4} flexDirection="column">
              <Grid.Item flexDirection="column">
                <Label>
                  加盟するマネー <Required />
                </Label>
                <Select
                  options={this.state.moneyOptions}
                  value={this.state.selectedMoneyOptions}
                  onChange={this.onSelectMoney}
                  isMulti="true"
                  placeholder="加盟するマネーを選択して下さい"
                  isDisabled
                />
              </Grid.Item>

              <Grid.Spacer style={{ marginBottom: '0.5rem' }} />

              <Grid.Item flexDirection="column">
                <Label>
                  組織コード <Required />
                </Label>
                <Input
                  style={{ backgroundColor: '#F5F5F5' }}
                  syncValue={this.state.org.code}
                  disabled
                  fullWidth
                />
              </Grid.Item>

              <Grid.Item flexDirection="column">
                <InputWithErrorWrapper>
                  <Label>事業体の種類</Label>
                  <RadioGroupWrapper>
                    <RadioWrapper>
                      <Radio
                        id="individual"
                        name="business-type"
                        type="radio"
                        value="individual"
                        checked={this.state.org.business_type === 'individual'}
                        onChange={(e) => this.setOrg('business_type', e.target.value)}
                      />
                      <Label htmlFor="individual">個人事業主</Label>
                    </RadioWrapper>
                    <RadioWrapper>
                      <Radio
                        id="corporate"
                        name="business-type"
                        type="radio"
                        value="corporate"
                        checked={this.state.org.business_type === 'corporate'}
                        onChange={(e) => this.setOrg('business_type', e.target.value)}
                      />
                      <Label htmlFor="corporate">法人</Label>
                    </RadioWrapper>
                  </RadioGroupWrapper>
                </InputWithErrorWrapper>
              </Grid.Item>

              {this.state.org.business_type === 'corporate' && (
                <Grid.Item flexDirection="column">
                  <InputWithErrorWrapper>
                    <Label>
                      法人番号 <Required />
                    </Label>
                    <Input
                      maxLength="13"
                      pattern="\d{13}"
                      title="法人番号は13桁の半角数字である必要があります"
                      syncValue={this.state.org.corporate_number ?? ''}
                      onChange={(e) => {
                        if (/^\d*$/.test(e.target.value)) {
                          this.setOrg('corporate_number', e.target.value);
                        }
                      }}
                      fullWidth
                    />
                    {this.state.validationErrors.corporate_number && (
                      <ErrorText>{this.state.validationErrors.corporate_number}</ErrorText>
                    )}
                  </InputWithErrorWrapper>
                </Grid.Item>
              )}
            </Grid.Item>

            <Grid.Item startColumn={3} size={6} flexDirection="column">
              <InputWithErrorWrapper>
                <Label>
                  組織名 <Required />
                </Label>
                <TextArea
                  maxLength="256"
                  value={this.state.org.name}
                  onChange={(e) => this.setOrg('name', e.target.value)}
                  fullWidth
                  rows={1}
                />
                {this.state.validationErrors.name && (
                  <ErrorText>{this.state.validationErrors.name}</ErrorText>
                )}
              </InputWithErrorWrapper>
            </Grid.Item>

            <Grid.Item startColumn={3} size={4} flexDirection="column">
              <Label>担当者名</Label>
              <Input
                syncValue={this.state.org.contact_name}
                onChange={(e) => this.setOrg('contact_name', e.target.value)}
                fullWidth
              />
            </Grid.Item>

            <Grid.Divider />

            {/* 振り込み先情報 section */}
            <Grid.Item size={2}>振り込み先情報</Grid.Item>

            <Grid.Item size={4} flexDirection="column">
              <Grid.Item flexDirection="column">
                <InputWithErrorWrapper>
                  <Label>金融機関名</Label>
                  <Input
                    syncValue={this.state.org.bank_name}
                    onChange={(e) => this.setOrg('bank_name', e.target.value)}
                    fullWidth
                  />
                  <Alert color="danger" isOpen={this.isInvalidParameter('bank_name')}>
                    金融機関名は64文字以下の文字列である必要があります
                  </Alert>
                </InputWithErrorWrapper>
              </Grid.Item>

              <Grid.Item flexDirection="column">
                <InputWithErrorWrapper>
                  <Label>金融機関コード</Label>
                  <Input
                    syncValue={this.state.org.bank_code}
                    pattern="\d{4}"
                    title="金融機関コードは4桁の半角数字である必要があります"
                    onChange={(e) => this.setOrg('bank_code', e.target.value)}
                    fullWidth
                  />
                  <Alert color="danger" isOpen={this.isInvalidParameter('bank_code')}>
                    金融機関コードは4桁の半角数字である必要があります
                  </Alert>
                </InputWithErrorWrapper>
              </Grid.Item>

              <Grid.Item flexDirection="column">
                <InputWithErrorWrapper>
                  <Label>支店名</Label>
                  <Input
                    syncValue={this.state.org.bank_branch_name}
                    onChange={(e) => this.setOrg('bank_branch_name', e.target.value)}
                    fullWidth
                  />
                  <Alert color="danger" isOpen={this.isInvalidParameter('bank_branch_name')}>
                    金融機関店舗名は64文字以下の文字列である必要があります
                  </Alert>
                </InputWithErrorWrapper>
              </Grid.Item>

              <Grid.Item flexDirection="column">
                <InputWithErrorWrapper>
                  <Label>支店コード</Label>
                  <Input
                    syncValue={this.state.org.bank_branch_code}
                    pattern="\d{3}"
                    title="金融機関店舗コードは3桁の半角数字である必要があります"
                    onChange={(e) => this.setOrg('bank_branch_code', e.target.value)}
                    fullWidth
                  />
                  <Alert color="danger" isOpen={this.isInvalidParameter('bank_branch_code')}>
                    金融機関店舗コードは3桁の半角数字である必要があります
                  </Alert>
                </InputWithErrorWrapper>
              </Grid.Item>

              <Grid.Item flexDirection="column">
                <Label>口座種別</Label>
                <RadioGroupWrapper>
                  <RadioWrapper>
                    <Radio
                      id="saving"
                      name="account-type"
                      type="radio"
                      value="saving"
                      checked={this.state.org.bank_account_type === 'saving'}
                      onChange={(e) => this.setOrg('bank_account_type', e.target.value)}
                    />
                    <Label htmlFor="saving">普通</Label>
                  </RadioWrapper>
                  <RadioWrapper>
                    <Radio
                      id="current"
                      name="account-type"
                      type="radio"
                      value="current"
                      checked={this.state.org.bank_account_type === 'current'}
                      onChange={(e) => this.setOrg('bank_account_type', e.target.value)}
                    />
                    <Label htmlFor="current">当座</Label>
                  </RadioWrapper>
                  <RadioWrapper>
                    <Radio
                      id="other"
                      name="account-type"
                      type="radio"
                      value="other"
                      checked={this.state.org.bank_account_type === 'other'}
                      onChange={(e) => this.setOrg('bank_account_type', e.target.value)}
                    />
                    <Label htmlFor="other">その他</Label>
                  </RadioWrapper>
                </RadioGroupWrapper>
              </Grid.Item>

              <Grid.Item flexDirection="column">
                <InputWithErrorWrapper>
                  <Label>口座番号</Label>
                  <Input
                    syncValue={this.state.org.bank_account}
                    pattern="\d{0,7}"
                    title="金融機関口座番号は7桁以下の半角数字である必要があります"
                    onChange={(e) => this.setOrg('bank_account', e.target.value)}
                    fullWidth
                  />
                  <Alert color="danger" isOpen={this.isInvalidParameter('bank_account')}>
                    金融機関口座番号は7桁以下の半角数字である必要があります
                  </Alert>
                </InputWithErrorWrapper>
              </Grid.Item>

              <Grid.Item flexDirection="column">
                <InputWithErrorWrapper>
                  <Label>口座名義人</Label>
                  <Input
                    syncValue={this.state.org.bank_account_holder_name}
                    pattern="^[0-9A-Zァ-ヶ　ｦ-ﾟ '＼￥，．ー「」（）／\\¥,.\-｢｣()/]+$"
                    title="金融機関口座名義人は30桁以下の半角数字、半角英字大文字、カナ文字、記号(¥,.-｢｣()/)である必要があります。また、全角は半角に変換されます。"
                    onChange={(e) => this.setOrg('bank_account_holder_name', e.target.value)}
                    onBlur={(e) =>
                      this.setOrg(
                        'bank_account_holder_name',
                        toJapaneseBankingSystemHalfWidth(toKatakana(e.target.value))
                      )
                    }
                    placeholder="例)ﾔﾏﾀﾞﾀﾛｳ"
                    fullWidth
                  />
                  <Alert
                    color="danger"
                    isOpen={this.isInvalidParameter('bank_account_holder_name')}
                  >
                    金融機関口座名義人は30桁以下の半角数字、半角英字大文字、カナ文字、記号(¥,.-｢｣()/)である必要があります。また、全角は半角に変換されます。
                  </Alert>
                </InputWithErrorWrapper>
              </Grid.Item>
            </Grid.Item>

            <Grid.Divider />

            {/* 各種費用 section */}
            <Grid.Item size={2}>各種費用</Grid.Item>

            <Grid.Item size={6} flexDirection="column">
              {this.state.isLoadingFees ? (
                <div>読み込み中...</div>
              ) : this.state.selectedMoneyOptions.length === 0 ? (
                <div>加盟するマネーがありません</div>
              ) : (
                this.state.selectedMoneyOptions.map((money) => (
                  <OrgMoneyFeeWrapper key={money.value.id}>
                    <Label style={{ fontWeight: 700 }}>{money.label}</Label>
                    <OrgMoneyFeeInputRowWrapper>
                      <OrgMoneyFeeInputWrapper>
                        <Label>決済手数料(％)</Label>
                        <Input
                          type="text"
                          inputMode="decimal"
                          syncValue={
                            this.state.moneyFees[money.value.id]?.payment_fee_rate_percentage || ''
                          }
                          onChange={(e) =>
                            this.setMoneyFee(
                              money.value.id,
                              'payment_fee_rate_percentage',
                              e.target.value
                            )
                          }
                          onBlur={(e) => {
                            const value = e.target.value;
                            if (value.endsWith('.')) {
                              this.setMoneyFee(
                                money.value.id,
                                'payment_fee_rate_percentage',
                                value.slice(0, -1)
                              );
                            }
                          }}
                          fullWidth
                        />
                      </OrgMoneyFeeInputWrapper>
                      <OrgMoneyFeeInputWrapper>
                        <Label>月額システム利用料</Label>
                        <LocalizedMask
                          value={this.state.moneyFees[money.value.id]?.system_fee || ''}
                          onChange={(e) =>
                            this.setMoneyFee(money.value.id, 'system_fee', e.target.value)
                          }
                        >
                          {({ value, ...inputProps }) => (
                            <Input {...inputProps} syncValue={value} fullWidth />
                          )}
                        </LocalizedMask>
                      </OrgMoneyFeeInputWrapper>
                      <OrgMoneyFeeInputWrapper>
                        <Label>その他固定費</Label>
                        <LocalizedMask
                          value={this.state.moneyFees[money.value.id]?.fixed_fee || ''}
                          onChange={(e) =>
                            this.setMoneyFee(money.value.id, 'fixed_fee', e.target.value)
                          }
                        >
                          {({ value, ...inputProps }) => (
                            <Input {...inputProps} syncValue={value} fullWidth />
                          )}
                        </LocalizedMask>
                      </OrgMoneyFeeInputWrapper>
                    </OrgMoneyFeeInputRowWrapper>
                  </OrgMoneyFeeWrapper>
                ))
              )}
            </Grid.Item>

            <Grid.Divider />

            <Grid.Item size={12}>
              <Button type="submit">更新</Button>
            </Grid.Item>
          </FormGrid>

          <Alert color="success" isOpen={this.isSuccess()}>
            決済加盟店の登録情報を更新しました
          </Alert>
        </form>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  user: state.user,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      showLoadingSpinner,
      hideLoadingSpinner,
    },
    dispatch
  );

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(CSSModules(UpdateMemberOrganization, styles));
